import { addDays, addMonths, DEMO_TODAY } from '../../lib/format';
import type { Random } from '../../lib/random';
import type {
  BeneficialOwner,
  Holder,
  HolderType,
  InvestorLogin,
  KycStatus,
  TfnStatus,
} from '../types';
import {
  ACCOUNTANTS,
  ADVISERS,
  FIRST_NAMES,
  FOREIGN_PLACES,
  LAST_NAMES,
  PLACES,
  STREETS,
} from './seedNames';
import { entityIdForHolder } from './seedEntities';
import { PERSONA_HOLDERS, PERSONA_LOGIN_ID, SECOND_LOGIN_ID } from './seedPersonas';
import { FIDES_TENANT_ID, HARBOUR_TENANT_ID } from './seedReference';

export { PERSONA_LOGIN_ID } from './seedPersonas';

/** JOINT is a joint holding: an INDIVIDUAL entity with two Owners (the platform has no joint type). */
type HolderShape = HolderType | 'JOINT';

const TYPE_WEIGHTS: HolderShape[] = [
  'INDIVIDUAL',
  'INDIVIDUAL',
  'INDIVIDUAL',
  'JOINT',
  'JOINT',
  'SMSF',
  'SMSF',
  'SMSF',
  'TRUST',
  'TRUST',
  'TRUST',
  'COMPANY',
  'COMPANY',
  'PARTNERSHIP',
];

function holderName(type: HolderShape, first: string, last: string, partner: string): string {
  switch (type) {
    case 'JOINT':
      return `${first} & ${partner} ${last}`;
    case 'SMSF':
      return `${last} Superannuation Pty Ltd ATF ${last} Super Fund`;
    case 'TRUST':
      return `${last} Investments Pty Ltd ATF ${last} Family Trust`;
    case 'COMPANY':
      return `${last} Holdings Pty Ltd`;
    case 'PARTNERSHIP':
      return `${last} & Co Partnership`;
    default:
      return `${first} ${last}`;
  }
}

function owners(
  random: Random,
  type: HolderShape,
  first: string,
  partner: string,
  last: string,
  idBase: string,
): BeneficialOwner[] {
  if (type === 'INDIVIDUAL') return [];
  const owner = (name: string, role: BeneficialOwner['role'], ownershipPct?: number) => ({
    id: `${idBase}-bo-${name.replace(/\W/g, '').toLowerCase()}`,
    name,
    role,
    ownershipPct,
    kycStatus: 'VERIFIED' as KycStatus,
    pep: false,
  });
  if (type === 'JOINT')
    return [owner(`${first} ${last}`, 'PARTNER', 50), owner(`${partner} ${last}`, 'PARTNER', 50)];
  if (type === 'COMPANY') {
    return [
      owner(`${first} ${last}`, 'DIRECTOR', 60),
      owner(`${partner} ${last}`, 'SHAREHOLDER', 40),
    ];
  }
  if (type === 'PARTNERSHIP') {
    const other = `${random.pick(FIRST_NAMES)} ${random.pick(LAST_NAMES)}`;
    return [owner(`${first} ${last}`, 'PARTNER', 50), owner(other, 'PARTNER', 50)];
  }
  return [
    owner(`${first} ${last}`, 'TRUSTEE'),
    owner(`${partner} ${last}`, 'TRUSTEE'),
    owner(`${first} ${last}`, 'BENEFICIARY'),
  ];
}

function tfnStatus(random: Random, type: HolderShape): TfnStatus {
  const roll = random.next();
  if (roll < 0.08) return 'NOT_QUOTED';
  if (roll < 0.11) return 'EXEMPT';
  if ((type === 'COMPANY' || type === 'PARTNERSHIP') && roll < 0.4) return 'ABN_QUOTED';
  return 'QUOTED';
}

function kycStatus(random: Random): KycStatus {
  const roll = random.next();
  if (roll < 0.04) return 'REFER';
  if (roll < 0.07) return 'EXPIRED';
  if (roll < 0.1) return 'PENDING';
  return 'VERIFIED';
}

export function generateHolder(random: Random, index: number, tenantId: string): Holder {
  const shape = random.pick(TYPE_WEIGHTS);
  const type: HolderType = shape === 'JOINT' ? 'INDIVIDUAL' : shape;
  const first = random.pick(FIRST_NAMES);
  const partner = random.pick(FIRST_NAMES);
  const last = random.pick(LAST_NAMES);
  const foreign = random.chance(0.07);
  const place = foreign ? random.pick(FOREIGN_PLACES) : { ...random.pick(PLACES), country: 'AU' };
  const id = `h-${tenantId === FIDES_TENANT_ID ? 'f' : 'h'}${String(index).padStart(3, '0')}`;
  const created = addDays('2023-03-01', random.int(0, 1200));
  const kyc = kycStatus(random);
  const certIssued = addDays(DEMO_TODAY, -random.int(30, 700));
  const tfn = foreign ? 'EXEMPT' : tfnStatus(random, shape);
  return {
    id,
    tenantId,
    investmentEntityId: entityIdForHolder(id),
    accountNumber: `${tenantId === FIDES_TENANT_ID ? 'FC' : 'HL'}${String(100_000 + index * 37)}`,
    name: holderName(shape, first, last, partner),
    type,
    contactName: `${first} ${last}`,
    email: `${first}.${last}${index}@example.com`.toLowerCase(),
    phone: `04${random.digits(2)} ${random.digits(3)} ${random.digits(3)}`,
    address: {
      line1: `${random.int(2, 220)} ${random.pick(STREETS)}`,
      suburb: place.suburb,
      state: place.state,
      postcode: place.postcode,
      country: place.country,
    },
    abn:
      type === 'INDIVIDUAL'
        ? undefined
        : `${random.digits(2)} ${random.digits(3)} ${random.digits(3)} ${random.digits(3)}`,
    acn:
      type === 'COMPANY' || type === 'TRUST' || type === 'SMSF'
        ? `${random.digits(3)} ${random.digits(3)} ${random.digits(3)}`
        : undefined,
    tfnStatus: tfn,
    tfnLast3: tfn === 'QUOTED' ? random.digits(3) : undefined,
    residency: place.country,
    fatca: place.country === 'US' ? 'REPORTABLE' : 'NOT_REPORTABLE',
    crs: foreign ? 'REPORTABLE' : 'NOT_REPORTABLE',
    kycStatus: kyc,
    kycVerifiedAt: kyc === 'VERIFIED' || kyc === 'EXPIRED' ? created : undefined,
    kycRefreshDue:
      kyc === 'EXPIRED' ? addDays(DEMO_TODAY, -random.int(5, 60)) : addMonths(created, 36),
    riskRating: foreign ? 'MEDIUM' : random.chance(0.05) ? 'HIGH' : 'LOW',
    pep: random.chance(0.03),
    wholesale: {
      basis: random.chance(0.85) ? 'ACCOUNTANT_CERTIFICATE' : 'PROFESSIONAL_INVESTOR',
      issuedAt: certIssued,
      expiresAt: addMonths(certIssued, 24),
      issuer: random.pick(ACCOUNTANTS),
    },
    bank: {
      bsb: `0${random.int(13, 86)}-${random.digits(3)}`,
      accountNumber: random.digits(9),
      accountName: holderName(shape, first, last, partner),
      verified: random.chance(0.95),
    },
    beneficialOwners: owners(random, shape, first, partner, last, id),
    drpElection: random.chance(0.3),
    communication: random.chance(0.92) ? 'EMAIL' : 'POST',
    adviser: random.chance(0.35) ? random.pick(ADVISERS) : undefined,
    createdAt: created,
  };
}

export function generateHolders(random: Random): Holder[] {
  const fides = Array.from({ length: 72 }, (_, index) =>
    generateHolder(random, index + 1, FIDES_TENANT_ID),
  );
  const harbour = Array.from({ length: 18 }, (_, index) =>
    generateHolder(random, index + 1, HARBOUR_TENANT_ID),
  );
  return [...PERSONA_HOLDERS, ...fides, ...harbour];
}

export function buildInvestorLogins(holders: Holder[]): InvestorLogin[] {
  const logins: InvestorLogin[] = [
    {
      id: PERSONA_LOGIN_ID,
      tenantId: FIDES_TENANT_ID,
      name: 'Olivia Bennett',
      email: 'olivia.bennett@example.com',
      holderIds: ['h-bennett-ind', 'h-bennett-smsf', 'h-bennett-trust'],
      mfaEnabled: true,
      lastLogin: '2026-10-14T19:42:00',
    },
    {
      id: SECOND_LOGIN_ID,
      tenantId: FIDES_TENANT_ID,
      name: 'Raj Mehta',
      email: 'raj.mehta@example.com',
      holderIds: ['h-mehta-co'],
      mfaEnabled: true,
      lastLogin: '2026-10-09T08:15:00',
    },
  ];
  holders
    .filter((holder) => holder.tenantId === FIDES_TENANT_ID && !holder.investorLoginId)
    .slice(0, 40)
    .forEach((holder, index) => {
      if (index % 3 === 2) return;
      const loginId = `il-${holder.id}`;
      holder.investorLoginId = loginId;
      logins.push({
        id: loginId,
        tenantId: holder.tenantId,
        name: holder.contactName,
        email: holder.email,
        holderIds: [holder.id],
        mfaEnabled: index % 4 !== 0,
        lastLogin: `2026-${String(8 + (index % 3)).padStart(2, '0')}-${String(1 + (index % 27)).padStart(2, '0')}T10:00:00`,
      });
    });
  return logins;
}
